import { useEffect, useRef, useState } from 'react';
import { api } from '../../core/api/client';
import { t } from '../../platform/lang';

interface Msg { id: number; body: string; route_change: boolean; created_at: string }

/**
 * MESSAGES FROM THE OFFICE (22 Oct). Shown at the top of the route screen until
 * the driver taps "Got it", which tells the office it was read. Asked for every
 * 30 seconds while the app is on screen. A message about a route change also
 * reloads the route, so a moved or added stop appears without reopening the app.
 */
export function OfficeMessages({ onRouteChange }: { onRouteChange: () => void }) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const reload = useRef(onRouteChange);
  reload.current = onRouteChange;
  const seen = useRef(new Set<number>());

  useEffect(() => {
    let stop = false;
    const ask = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const r = await api.officeMessages();
        if (stop) return;
        const fresh = r.messages.filter((m) => !seen.current.has(m.id));
        fresh.forEach((m) => seen.current.add(m.id));
        if (fresh.some((m) => m.route_change)) reload.current();
        setMsgs(r.messages);
      } catch { /* no signal: try again next time */ }
    };
    void ask();
    const timer = setInterval(() => void ask(), 30_000);
    const vis = () => void ask();
    document.addEventListener('visibilitychange', vis);
    return () => { stop = true; clearInterval(timer); document.removeEventListener('visibilitychange', vis); };
  }, []);

  if (msgs.length === 0) return null;
  return (
    <div className="office-msgs">
      {msgs.map((m) => (
        <div key={m.id} className="banner warn-banner" role="alert">
          <strong>{t('officeMessage')}</strong>
          <p style={{ margin: '4px 0 8px' }}>{m.body}</p>
          <button className="btn primary" onClick={() => {
            setMsgs((x) => x.filter((y) => y.id !== m.id));
            void api.readOfficeMessage(m.id).catch(() => undefined);
          }}>{t('gotIt')}</button>
        </div>
      ))}
    </div>
  );
}
